import { Env } from "../settings/Env";
import { MeshName } from "./MeshName";
import { DcRenderingGroup } from "./DcRenderingGroup";
import { MeshUtils } from "@dreamcam/babylon-tools";
import { Observable } from "@babylonjs/core/Misc/observable.js";
import { Ray } from "@babylonjs/core/Culling/ray.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.js";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder.js";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes.js";
import { CompatibilityOptionsImpl } from "../settings/CompatibilityOptions";
const POINTER_ADDON_NAME = "pointerAddon";
class PointerAddonImpl {
    constructor(mesh) {
        this._counter = 0;
        this.onPointerEnter = new Observable();
        this.onPointerOut = new Observable();
        this._mesh = mesh;
    }
    static _isMesh2D(value) {
        return value.mesh !== undefined;
    }
    static _getAbstractMesh(mesh) {
        return PointerAddonImpl._isMesh2D(mesh) ? mesh.mesh : mesh;
    }
    enter() {
        this._counter++;
        if (this._counter === 1) {
            this.onPointerEnter.notifyObservers(PointerAddonImpl._getAbstractMesh(this._mesh));
        }
    }
    out() {
        this._counter--;
        if (this._counter === 0) {
            this.onPointerOut.notifyObservers(PointerAddonImpl._getAbstractMesh(this._mesh));
        }
    }
    reset() {
        while (this.isFocused) {
            this.out();
        }
    }
    get isFocused() {
        return this._counter > 0;
    }
}
const CLICK_ADDON_NAME = "clickAddon";
const VISIBILITY_CHANGE_NAME = "visibilitychange";
const INPUT_SOURCES_CHANGE_NAME = "inputsourceschange";
export class ClickAddon {
    constructor() {
        this.onClick = new Observable();
    }
}
const HAND_PROFILES = new Set([
    "oculus-hand",
    "generic-hand",
    "generic-hand-select",
]);
export var ControllerButton;
(function (ControllerButton) {
    ControllerButton[ControllerButton["Trigger"] = 0] = "Trigger";
    ControllerButton[ControllerButton["Button0"] = 1] = "Button0";
    ControllerButton[ControllerButton["Button1"] = 2] = "Button1";
    ControllerButton[ControllerButton["ThumbRest"] = 3] = "ThumbRest";
    ControllerButton[ControllerButton["ThumbstickButton"] = 4] = "ThumbstickButton";
    ControllerButton[ControllerButton["Squeeze"] = 5] = "Squeeze";
})(ControllerButton || (ControllerButton = {}));
export var ControllerAxes;
(function (ControllerAxes) {
    ControllerAxes[ControllerAxes["Trigger"] = 0] = "Trigger";
    ControllerAxes[ControllerAxes["Thumbstick"] = 1] = "Thumbstick";
    ControllerAxes[ControllerAxes["Squeeze"] = 2] = "Squeeze";
})(ControllerAxes || (ControllerAxes = {}));
class XRMode {
    constructor(commonAPI, xrExperience, scene) {
        this._controllers = {};
        this._setControllerVisibility = (controllerData, isVisible) => {
            controllerData.visibility.controller = isVisible;
            controllerData.visibility.laserPointer = isVisible;
            controllerData.visibility.selectionMesh = isVisible;
            this._updateControllerVisibility(controllerData);
        };
        this._setControllersVisibility = (isVisible) => {
            Object.keys(this._controllers).forEach((id) => this._setControllerVisibility(this._controllers[id], isVisible));
        };
        this._headsetStateChanged = (eventData) => {
            this._setControllersVisibility(eventData.session.visibilityState === "visible");
        };
        this._pointers = [];
        this._inputChanged = () => {
            this._pointers.length = 0;
        };
        this._isHandProfile = (profiles) => {
            if (!profiles)
                return false;
            return profiles.some(p => HAND_PROFILES.has(p));
        };
        this._findController = (pointerInfo) => {
            const pointerId = pointerInfo.event.pointerId;
            if (this._pointers.some(p => p.pointerId === pointerId))
                return;
            const controllerId = this._xrExperience.pointerSelection.getXRControllerByPointerId(pointerId)?.uniqueId;
            if (controllerId !== undefined) {
                this._pointers.push({ pointerId, controllerId });
            }
        };
        this._cameraSelectEnd = () => {
            const ray = new Ray(new Vector3(), new Vector3());
            this._xrExperience.baseExperience.camera.getForwardRayToRef(ray);
            const pick = this._scene.pickWithRay(ray);
            if (pick !== null) {
                this._commonAPI.invokeMeshHitCallback(pick);
            }
        };
        this._onControllerRemovedCallback = (inputSource) => {
            this._disposeController(inputSource.uniqueId);
        };
        this._onControllerAddedCallback = (inputSource) => {
            // console.warn("+++ onControllerAddedCallback");
            this._initInputSource(inputSource);
        };
        this._initInputSource = (controller) => {
            const handTrackingEnabled = CompatibilityOptionsImpl.getInstance().data?.enableHandTracking !== false;
            const isHandInputSource = this._isHandProfile(controller.inputSource.profiles);
            console.log("XRInputHelper: init inputSource", {
                uniqueId: controller.uniqueId,
                handTrackingEnabled,
                isHandInputSource,
                targetRayMode: controller.inputSource.targetRayMode,
                handedness: controller.inputSource.handedness,
                profiles: controller.inputSource.profiles,
                hasGamepad: controller.inputSource.gamepad !== undefined,
                hasMotionController: controller.motionController !== null,
            });
            if (!handTrackingEnabled && isHandInputSource) {
                const pointerSelectionControllers = this._xrExperience.pointerSelection?._controllers;
                if (pointerSelectionControllers && controller.uniqueId in pointerSelectionControllers) {
                    delete pointerSelectionControllers[controller.uniqueId];
                }
                controller.pointer.isVisible = false;
                controller.pointer.setEnabled(false);
                controller.onMeshLoadedObservable.addOnce((mesh) => {
                    mesh.setEnabled(false);
                    mesh.isVisible = false;
                    mesh.getChildMeshes(false).forEach((child) => {
                        child.setEnabled(false);
                        child.isVisible = false;
                    });
                });
                return;
            }
            // Turn off gaze control:
            if (controller.inputSource.targetRayMode === "gaze") {
                controller.pointer.isVisible = false;
                controller.pointer.setEnabled(false);
                // We should add our custom gaze pointer here:
                //...
                return;
            }
            if (controller.inputSource.targetRayMode === "tracked-pointer") {
                const cameraController = this._controllers["camera"];
                if (cameraController) {
                    delete this._controllers["camera"];
                    cameraController.selectionMesh.dispose();
                    this._xrExperience.baseExperience.sessionManager.session.removeEventListener("selectend", this._cameraSelectEnd);
                }
            }
            if (controller.motionController) {
                this._initMotionController(controller, controller.motionController);
            }
            else if (CompatibilityOptionsImpl.getInstance().data?.visionPro?.customTransientPointerHandler !== false && controller.inputSource.targetRayMode === "transient-pointer" && CompatibilityOptionsImpl.getInstance().data?.visionPro?.enableGazeInput === false) {
                const controllerData = {
                    xrController: controller,
                    controllerRootMesh: null,
                    laserPointer: this._createLaserPointer(controller.pointer),
                    selectionMesh: this._createSelectionMesh(),
                    meshUnderPointer: null,
                    tmpRay: new Ray(new Vector3(), new Vector3()),
                    pick: null,
                    visibility: {
                        controller: false,
                        laserPointer: true,
                        selectionMesh: true
                    }
                };
                this._controllers[controller.uniqueId] = controllerData;
                this._xrExperience.baseExperience.sessionManager.session.addEventListener("selectend", () => {
                    if (this._xrExperience.input.controllers.length > 0) {
                        const ray = new Ray(new Vector3(), new Vector3());
                        this._xrExperience.input.controllers[0].getWorldPointerRayToRef(ray);
                        const pick = this._scene.pickWithRay(ray);
                        if (pick !== null) {
                            this._commonAPI.invokeMeshHitCallback(pick);
                        }
                        const controllerData = this._controllers[controller.uniqueId];
                        if (!controllerData)
                            return;
                        this._unfocusController(controllerData);
                        this._setControllerVisibility(controllerData, false);
                        controllerData.selectionMesh?.dispose();
                        controllerData.laserPointer?.dispose();
                        delete this._controllers[controller.uniqueId];
                    }
                }, { once: true });
            }
            else {
                controller.onMotionControllerInitObservable.add((eventData) => {
                    this._initMotionController(controller, eventData);
                });
            }
        };
        this._onXRFrame = (_frame) => {
            Object.keys(this._controllers).forEach((id) => {
                const controllerData = this._controllers[id];
                const handTrackingEnabled = CompatibilityOptionsImpl.getInstance().data?.enableHandTracking !== false;
                const isHandInputSource = this._isHandProfile(controllerData?.xrController?.inputSource?.profiles);
                if (!handTrackingEnabled && isHandInputSource) {
                    return;
                }
                // Every frame check collisions/input
                if (id === "camera")
                    this._xrExperience.baseExperience.camera.getForwardRayToRef(controllerData.tmpRay);
                else
                    controllerData.xrController.getWorldPointerRayToRef(controllerData.tmpRay);
                controllerData.pick = this._scene.pickWithRay(controllerData.tmpRay);
                // controllerData.pick = this.inputEnabled
                //     ? this._scene.pickWithRay(controllerData.tmpRay)
                //     : null;
                this._commonAPI.invokePointerCallback(controllerData.tmpRay?.direction, this._pointers.find(p => p.controllerId === id)?.pointerId ?? 0);
                // controllerData.laserPointer.isVisible = this.inputEnabled;
                const pick = controllerData.pick;
                if (pick && pick.pickedPoint && pick.hit) {
                    // Update cursor state
                    controllerData.selectionMesh.position.copyFrom(pick.pickedPoint);
                    const scaling = Math.sqrt(pick.distance);
                    controllerData.selectionMesh.scaling.x = scaling;
                    controllerData.selectionMesh.scaling.y = scaling;
                    controllerData.selectionMesh.scaling.z = scaling;
                    const pickedMesh = pick.pickedMesh;
                    controllerData.selectionMesh.isVisible = pick.hit && this._isPickableMesh(pickedMesh) &&
                        controllerData.visibility.selectionMesh;
                    if (controllerData.meshUnderPointer !== pickedMesh) {
                        if (controllerData.pointerAddon) {
                            controllerData.pointerAddon.out();
                        }
                        const pointerAddon = pickedMesh[POINTER_ADDON_NAME];
                        if (pointerAddon) {
                            pointerAddon.enter();
                        }
                        controllerData.meshUnderPointer = pickedMesh;
                        controllerData.pointerAddon = pointerAddon;
                    }
                }
                else {
                    controllerData.selectionMesh.isVisible = false;
                    this._unfocusController(controllerData);
                }
            });
        };
        this._isPickableMesh = (mesh) => {
            if (mesh === null || !mesh.isPickable)
                return false;
            return mesh.name !== MeshName.backgroundSkybox &&
                mesh.name !== MeshName.projectionMeshLeft && mesh.name !== MeshName.projectionMeshRight;
        };
        this._initMotionController = (controller, motionController) => {
            const controllerData = {
                xrController: controller,
                controllerRootMesh: null,
                laserPointer: this._createLaserPointer(controller.pointer),
                selectionMesh: this._createSelectionMesh(),
                meshUnderPointer: null,
                tmpRay: new Ray(new Vector3(), new Vector3()),
                pick: null,
                visibility: {
                    controller: true,
                    laserPointer: false,
                    selectionMesh: false
                }
            };
            this._initTriggerComponent(controller, motionController);
            this._initThumbstickComponent(controller, motionController);
            this._initButtonsComponents(controller, motionController);
            this._initAxesComponents(controller, motionController);
            this._controllers[controller.uniqueId] = controllerData;
            controller.onMeshLoadedObservable.addOnce((mesh) => {
                MeshUtils.setRenderingGroup(mesh, DcRenderingGroup.pointerAndLaser);
                controllerData.controllerRootMesh = mesh;
                const isVisible = controllerData.visibility.controller;
                controllerData.visibility.laserPointer = isVisible;
                controllerData.visibility.selectionMesh = isVisible;
                this._updateControllerVisibility(controllerData);
            });
            // console.log(`++++ added controller: ${this._getControllersCount()}`);
        };
        this._unfocusController = (controllerData) => {
            if (controllerData.pointerAddon) {
                controllerData.pointerAddon.out();
                controllerData.pointerAddon = undefined;
            }
            controllerData.meshUnderPointer = null;
        };
        this._updateControllerVisibility = (controllerData) => {
            if (controllerData.laserPointer)
                controllerData.laserPointer.isVisible = controllerData.visibility.laserPointer;
            if (controllerData.controllerRootMesh !== null) {
                MeshUtils.setRenderingGroup(controllerData.controllerRootMesh, DcRenderingGroup.pointerAndLaser);
                controllerData.controllerRootMesh.isVisible = controllerData.visibility.controller;
                const childMeshes = controllerData.controllerRootMesh.getChildMeshes(false);
                childMeshes.forEach((mesh) => mesh.isVisible = controllerData.visibility.controller);
            }
        };
        this._initTriggerComponent = (controller, motionController) => {
            const invokeHitIfPressed = (controller, component) => {
                // if (this.inputEnabled) return;
                const isPressed = component.hasChanges && component.changes.pressed !== undefined &&
                    !component.changes.pressed.previous && component.changes.pressed.current;
                if (!isPressed)
                    return;
                const controllerData = this._controllers[controller.uniqueId];
                if (!controllerData)
                    return;
                this._commonAPI.invokeMeshHitCallback(controllerData.pick);
            };
            // WebXR has an issue with Google Daydream touchpad.
            // See https://forum.babylonjs.com/t/xr-controller-issue/15186 for more information.
            // hasOnlyTouchpad variable is our workaround.
            let hasOnlyTouchpad = true;
            motionController.getAllComponentsOfType("trigger").forEach((component) => {
                hasOnlyTouchpad = false;
                component.onButtonStateChangedObservable.add(() => invokeHitIfPressed(controller, component));
            });
            if (hasOnlyTouchpad) {
                motionController.getAllComponentsOfType("touchpad").forEach((component) => {
                    component._buttonIndex = 0; // A _PATCH_ directly for Google Daydream!
                    component.onButtonStateChangedObservable.add(() => invokeHitIfPressed(controller, component));
                });
            }
        };
        this._initThumbstickComponent = (controller, motionController) => {
            const invokeThumbstickCallback = (controller, value) => {
                const controllerData = this._controllers[controller.uniqueId];
                if (!controllerData)
                    return;
                this._commonAPI.invokeThumbstickAxesChangedCallback(controllerData.pick, controller.uniqueId, value);
            };
            motionController.getAllComponentsOfType("thumbstick").forEach((component) => {
                component.onAxisValueChangedObservable.add((e) => invokeThumbstickCallback(controller, e));
            });
        };
        this._trySetButtonCallback = (motionController, controller, componentId, buttonId) => {
            const component = motionController.getComponent(componentId);
            if (component === null || component === undefined)
                return false;
            component.onButtonStateChangedObservable.add((e) => {
                const controllerData = this._controllers[controller.uniqueId];
                this._commonAPI.invokeButtonCallback(buttonId, controller.uniqueId, controllerData?.pick ?? null, e.pressed, e.value);
            });
            return true;
        };
        this._initButtonsComponents = (controller, motionController) => {
            this._trySetButtonCallback(motionController, controller, "xr-standard-trigger", ControllerButton.Trigger);
            this._trySetButtonCallback(motionController, controller, "a-button", ControllerButton.Button0);
            this._trySetButtonCallback(motionController, controller, "x-button", ControllerButton.Button0);
            this._trySetButtonCallback(motionController, controller, "b-button", ControllerButton.Button1);
            this._trySetButtonCallback(motionController, controller, "y-button", ControllerButton.Button1);
            this._trySetButtonCallback(motionController, controller, "thumbrest", ControllerButton.ThumbRest);
            this._trySetButtonCallback(motionController, controller, "xr-standard-thumbstick", ControllerButton.ThumbstickButton);
            this._trySetButtonCallback(motionController, controller, "xr-standard-squeeze", ControllerButton.Squeeze);
        };
        this._trySetAxesCallback = (motionController, controller, componentId, axesId) => {
            const component = motionController.getComponent(componentId);
            if (component === null || component === undefined)
                return false;
            component.onAxisValueChangedObservable.add((e) => {
                const controllerData = this._controllers[controller.uniqueId];
                this._commonAPI.invokeAxesCallback(axesId, controller.uniqueId, controllerData?.pick ?? null, e);
            });
            return true;
        };
        this._initAxesComponents = (controller, motionController) => {
            this._trySetAxesCallback(motionController, controller, "xr-standard-trigger", ControllerAxes.Trigger);
            this._trySetAxesCallback(motionController, controller, "xr-standard-thumbstick", ControllerAxes.Thumbstick);
            this._trySetAxesCallback(motionController, controller, "xr-standard-squeeze", ControllerAxes.Squeeze);
        };
        this._commonAPI = commonAPI;
        this._scene = scene;
        this._xrExperience = xrExperience;
    }
    changePointerState(isHighlighted) {
        Object.keys(this._controllers).forEach((id) => {
            this._controllers[id].selectionMesh.material.emissiveColor = isHighlighted ? Color3.Blue() : Color3.White();
            this._controllers[id].selectionMesh.material.diffuseColor = isHighlighted ? Color3.Blue() : Color3.White();
        });
    }
    get pointers() {
        return this._pointers;
    }
    activate() {
        this._xrExperience.input.onControllerAddedObservable.add(this._onControllerAddedCallback);
        this._xrExperience.input.onControllerRemovedObservable.add(this._onControllerRemovedCallback);
        if (CompatibilityOptionsImpl.getInstance().data?.visionPro?.enableGazeInput !== false && !Object.keys(this._controllers).some(k => this._controllers[k].xrController?.inputSource.targetRayMode === "tracked-pointer")) {
            const controllerData = {
                xrController: null,
                controllerRootMesh: null,
                laserPointer: null,
                selectionMesh: this._createSelectionMesh(),
                meshUnderPointer: null,
                tmpRay: new Ray(new Vector3(), new Vector3()),
                pick: null,
                visibility: {
                    controller: true,
                    laserPointer: false,
                    selectionMesh: false
                }
            };
            this._controllers["camera"] = controllerData;
            const isVisible = controllerData.visibility.controller;
            controllerData.visibility.laserPointer = isVisible;
            controllerData.visibility.selectionMesh = isVisible;
            this._updateControllerVisibility(controllerData);
            this._xrExperience.baseExperience.sessionManager.session.addEventListener("selectend", this._cameraSelectEnd);
        }
        else {
            this._xrExperience.input.controllers.forEach((controller) => {
                this._initInputSource(controller);
            });
        }
        this._xrExperience.baseExperience.sessionManager.onXRFrameObservable.add(this._onXRFrame);
        this._xrExperience.baseExperience.sessionManager.session.removeEventListener(VISIBILITY_CHANGE_NAME, this._headsetStateChanged);
        this._xrExperience.baseExperience.sessionManager.session.addEventListener(VISIBILITY_CHANGE_NAME, this._headsetStateChanged);
        this._xrExperience.baseExperience.sessionManager.session.addEventListener(INPUT_SOURCES_CHANGE_NAME, this._inputChanged);
        this._scene.onPointerObservable.add(this._findController);
    }
    deactivate() {
        this._xrExperience.baseExperience.sessionManager.session?.removeEventListener(VISIBILITY_CHANGE_NAME, this._headsetStateChanged);
        this._xrExperience.baseExperience.sessionManager.onXRFrameObservable.removeCallback(this._onXRFrame);
        this._xrExperience.input.onControllerAddedObservable.removeCallback(this._onControllerAddedCallback);
        this._xrExperience.input.onControllerRemovedObservable.removeCallback(this._onControllerRemovedCallback);
        this._xrExperience.baseExperience.sessionManager.session?.removeEventListener(INPUT_SOURCES_CHANGE_NAME, this._inputChanged);
        this._scene.onPointerObservable.removeCallback(this._findController);
        Object.keys(this._controllers).forEach((controllerUniqueId) => {
            this._disposeController(controllerUniqueId);
        });
        this._commonAPI.resetPointerAddons();
    }
    // public inputEnabled = true;
    //
    _disposeController(controllerUniqueId) {
        const controllerData = this._controllers[controllerUniqueId];
        if (!controllerData)
            return;
        this._unfocusController(controllerData);
        this._setControllerVisibility(controllerData, false);
        controllerData.selectionMesh?.dispose();
        controllerData.laserPointer?.dispose();
        delete this._controllers[controllerUniqueId];
    }
    /**
     * Pulse (vibrate) all controllers.
     * @param value01 – the strength of the pulse in 0.0…1.0 range
     * @param durationMillis – Duration of the pulse in milliseconds
     */
    pulse(value01, durationMillis) {
        Object.keys(this._controllers).forEach((controllerUniqueId) => {
            const controllerData = this._controllers[controllerUniqueId];
            (async () => {
                const pulseResult = await controllerData?.xrController?.motionController?.pulse(value01, durationMillis) || false;
                if (!pulseResult)
                    console.warn(`Pulse feature is not available`);
            })();
        });
    }
    _updatePointerDistance(laserPointer, distance) {
        laserPointer.scaling.y = distance;
        if (this._scene.useRightHandedSystem) {
            distance *= -1;
        }
        // a bit of distance from the controller
        laserPointer.position.z = distance / 2 + 0.05;
    }
    _createLaserPointer(meshParent) {
        const laserPointer = CreateCylinder("laserPointer", {
            height: 1,
            diameterTop: Env.controllerPointer.ray.diameterFar,
            diameterBottom: Env.controllerPointer.ray.diameterNear,
            tessellation: 20,
            subdivisions: 1,
        }, this._scene);
        laserPointer.bakeCurrentTransformIntoVertices();
        laserPointer.parent = meshParent;
        const laserPointerMaterial = new StandardMaterial("laserPointerMat", this._scene);
        laserPointerMaterial.emissiveColor = Color3.White();
        laserPointerMaterial.alpha = Env.controllerPointer.ray.alpha;
        laserPointer.material = laserPointerMaterial;
        laserPointer.renderingGroupId = DcRenderingGroup.pointerAndLaser; // (to render ray pointer above GUI elements)
        laserPointer.rotation.x = Math.PI / 2;
        this._updatePointerDistance(laserPointer, Env.controllerPointer.ray.length);
        laserPointer.isPickable = false;
        laserPointer.isVisible = false;
        return laserPointer;
    }
    _createSelectionMesh() {
        const selectionMesh = CreateSphere("selectionMesh", { diameter: Env.controllerPointer.dot.diameter }, this._scene);
        selectionMesh.bakeCurrentTransformIntoVertices();
        selectionMesh.isPickable = false;
        selectionMesh.isVisible = false;
        const targetMat = new StandardMaterial("targetMat", this._scene);
        targetMat.specularColor = Color3.Black();
        targetMat.emissiveColor = Color3.White();
        targetMat.backFaceCulling = false;
        selectionMesh.material = targetMat;
        selectionMesh.renderingGroupId = DcRenderingGroup.pointerAndLaser;
        return selectionMesh;
    }
}
class FlatMode {
    get inputEnabled() {
        return this._inputEnabled;
    }
    set inputEnabled(value) {
        this._inputEnabled = value;
        this._noFocus();
        this._isDrag = false;
        this._isPointerDown = false;
    }
    constructor(commonAPI, scene) {
        this._isDrag = false;
        this._isPointerDown = false;
        this._inputEnabled = true;
        this._onWindowPointerDown = () => {
            if (!this._inputEnabled)
                return;
            this._isDrag = false;
            this._isPointerDown = true;
        };
        this._onWindowPointerMove = () => {
            if (!this._inputEnabled)
                return;
            if (!this._isPointerDown)
                return;
            this._isDrag = true;
        };
        this._onWindowWheel = (event) => {
            if (!this._inputEnabled)
                return;
            const pickedMesh = this._scene.pick(event.x, event.y);
            this._commonAPI.invokeThumbstickAxesChangedCallback(pickedMesh, "", { x: event.deltaX / 100, y: event.deltaY / 100 });
            setTimeout(() => { this._commonAPI.invokeThumbstickAxesChangedCallback(pickedMesh, "", { x: 0, y: 0 }); }, 50);
        };
        this._onWindowPointerUp = (event) => {
            if (!this._inputEnabled)
                return;
            this._isPointerDown = false;
            if (this._isDrag)
                return;
            if (event.button !== 0)
                return;
            const pickedMesh = this._scene.pick(event.x, event.y);
            this._commonAPI.invokeMeshHitCallback(pickedMesh);
        };
        this._onPointerObservable = (pointerInfo) => {
            if (!this._inputEnabled)
                return;
            const pickingInfo = this._scene.pick(pointerInfo.event.x, pointerInfo.event.y);
            this._commonAPI.invokePointerCallback(pickingInfo?.ray?.direction, 0);
            if (pickingInfo === null || !pickingInfo.pickedMesh) {
                this._noFocus();
                return;
            }
            const pickedMesh = pickingInfo.pickedMesh;
            if (!pickedMesh) {
                this._noFocus();
                return;
            }
            else if (this._lastFocusedMesh && pickedMesh === this._lastFocusedMesh.mesh) {
                return;
            }
            this._noFocus();
            const pointerAddon = pickedMesh[POINTER_ADDON_NAME];
            if (!pointerAddon)
                return;
            this._lastFocusedMesh = {
                mesh: pickedMesh,
                pointerAddon
            };
            pointerAddon.enter();
        };
        this._commonAPI = commonAPI;
        this._scene = scene;
    }
    activate() {
        window.addEventListener("pointerdown", this._onWindowPointerDown);
        window.addEventListener("pointermove", this._onWindowPointerMove);
        window.addEventListener("pointerup", this._onWindowPointerUp);
        window.addEventListener("wheel", this._onWindowWheel);
        this._scene.onPointerObservable.add(this._onPointerObservable);
    }
    deactivate() {
        window.removeEventListener("pointerdown", this._onWindowPointerDown);
        window.removeEventListener("pointermove", this._onWindowPointerMove);
        window.removeEventListener("pointerup", this._onWindowPointerUp);
        window.removeEventListener("wheel", this._onWindowWheel);
        this._scene.onPointerObservable.removeCallback(this._onPointerObservable);
        this._commonAPI.resetPointerAddons();
    }
    _noFocus() {
        if (this._lastFocusedMesh) {
            this._lastFocusedMesh.pointerAddon.out();
            this._lastFocusedMesh = undefined;
        }
    }
}
export class XRInputHelper {
    get inputsEnabled() {
        return this._flatMode.inputEnabled;
    }
    set inputsEnabled(value) {
        this._flatMode.inputEnabled = value;
        // if (this._xrMode) {
        //     console.log(`%c xrMode.inputEnabled = ${value}`, "background: lime");
        //     this._xrMode.inputEnabled = value;
        // }
    }
    constructor(scene, xrHelper, xrInputPermissions) {
        this._idGenerator = 0;
        this._clickAwareMeshes = {};
        this._thumbstickAwareMeshes = {};
        this._buttonActions = {};
        this._axesActions = {};
        this._thumbstickAxesChangedTimes = {};
        this._pointerListeners = [];
        this._pointerAddons = [];
        this._commonAPI = {
            resetPointerAddons() {
                XRInputHelper.getInstance()?._pointerAddons.forEach(pa => pa.reset());
            },
            invokePointerCallback: (direction, pointerId) => {
                this._pointerListeners.forEach(l => l(direction, pointerId));
            },
            // The mesh MUST have name to invoke callback.
            invokeMeshHitCallback: (pickingInfo) => {
                if (this._xrInputPermissions?.isClickPermitted() !== false) {
                    Object.keys(this._clickAwareMeshes).forEach((key) => {
                        const item = this._clickAwareMeshes[key];
                        if (item !== undefined && this._xrInputPermissions?.isClickPermitted() !== false) {
                            let isHit = false;
                            if (pickingInfo && pickingInfo.pickedMesh && pickingInfo.pickedMesh.name !== "") {
                                isHit = item.meshes.indexOf(pickingInfo.pickedMesh.name) >= 0;
                                // console.log(`mesh=${pickingInfo.pickedMesh.name} isHit=${isHit}`);
                            }
                            item.callback(isHit);
                        }
                    });
                    if (pickingInfo) {
                        const pickedMesh = pickingInfo.pickedMesh;
                        if (pickedMesh) {
                            const clickAddon = pickingInfo.pickedMesh[CLICK_ADDON_NAME];
                            if (clickAddon) {
                                clickAddon.onClick.notifyObservers(pickedMesh);
                            }
                        }
                    }
                }
            },
            invokeThumbstickAxesChangedCallback: (pickingInfo, id, value) => {
                if (this._xrInputPermissions?.isClickPermitted() !== false) {
                    Object.keys(this._thumbstickAwareMeshes).forEach((key) => {
                        const item = this._thumbstickAwareMeshes[key];
                        if (pickingInfo && pickingInfo.pickedMesh && pickingInfo.pickedMesh.name !== "") {
                            if (item.meshes.indexOf(pickingInfo.pickedMesh.name) >= 0) {
                                const now = Date.now();
                                if (this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name] === undefined)
                                    this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name] = {};
                                if (this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name][id] === undefined)
                                    this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name][id] = now;
                                const delta = now - this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name][id];
                                this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name][id] = now;
                                const x = Math.abs(value.x) < 0.01 ? 0 : value.x;
                                const y = Math.abs(value.y) < 0.01 ? 0 : value.y;
                                item.callback(pickingInfo.pickedMesh, x, y, delta);
                                if (x === 0 && y === 0)
                                    this._thumbstickAxesChangedTimes[pickingInfo.pickedMesh.name][id] = undefined;
                            }
                        }
                    });
                }
            },
            invokeButtonCallback: (button, controllerId, pick, isPressed, value) => {
                Object.keys(this._buttonActions).forEach((key) => {
                    const item = this._buttonActions[key];
                    if (item.button === button && this._xrInputPermissions?.isClickPermitted() !== false) {
                        item.callback({ pick, controllerId, isPressed, value });
                    }
                });
            },
            invokeAxesCallback: (axes, controllerId, pick, value) => {
                Object.keys(this._axesActions).forEach((key) => {
                    const item = this._axesActions[key];
                    if (item.axes === axes && this._xrInputPermissions?.isClickPermitted() !== false) {
                        item.callback({ pick, controllerId, value });
                    }
                });
            },
        };
        this._xrStateChangedHandler = (xrState, _eventState) => {
            // console.log(`XR state: ${WebXRState[xrState]}`);
            switch (xrState) {
                case WebXRState.ENTERING_XR:
                    this._flatMode.deactivate();
                    break;
                case WebXRState.IN_XR:
                    this._xrMode?.activate();
                    break;
                case WebXRState.EXITING_XR:
                    this._xrMode?.deactivate();
                    break;
                case WebXRState.NOT_IN_XR:
                    this._xrMode?.deactivate(); // (WebXRState.EXITING_XR doesn't happen by some reason...)
                    this._flatMode.activate();
                    break;
            }
        };
        this._flatMode = new FlatMode(this._commonAPI, scene);
        this._xrHelper = xrHelper;
        if (this._xrHelper) {
            this._xrHelper.baseExperience.onStateChangedObservable.add(this._xrStateChangedHandler);
            this._xrMode = new XRMode(this._commonAPI, this._xrHelper, scene);
        }
        this._flatMode.activate();
        this._xrInputPermissions = xrInputPermissions;
        // this._testRecenter();
        // this._testResetViewEvent(this._xrExperience);
    }
    static initialize(scene, xrHelper, xrInputPermissions) {
        if (XRInputHelper._instance) {
            //throw new Error("XRInputHelper has been already initialized.");
            return;
        }
        XRInputHelper._instance = new XRInputHelper(scene, xrHelper, xrInputPermissions);
    }
    static getInstance() {
        if (!XRInputHelper._instance) {
            return null;
        }
        return XRInputHelper._instance;
    }
    dispose() {
        this._flatMode.deactivate();
        this._xrMode?.deactivate();
        this._xrHelper?.baseExperience.onStateChangedObservable.removeCallback(this._xrStateChangedHandler);
    }
    pulse() {
        this._xrMode?.pulse(1.0, 200);
    }
    get pointers() {
        return this._xrMode?.pointers ?? [{ pointerId: 0, controllerId: "" }];
    }
    changePointerState(isHighlighted) {
        this._xrMode?.changePointerState(isHighlighted);
    }
    addPointerListener(callback) {
        if (!this._pointerListeners.includes(callback))
            this._pointerListeners.push(callback);
    }
    removePointerListener(callback) {
        if (this._pointerListeners.includes(callback))
            this._pointerListeners.splice(this._pointerListeners.indexOf(callback), 1);
    }
    addClickOnMeshListener(meshes, callback) {
        const id = this._idGenerator++;
        const key = `${id}`;
        this._clickAwareMeshes[key] = { meshes, callback };
        return id;
    }
    removeClickOnMeshListener(handler) {
        delete this._clickAwareMeshes[`${handler}`];
    }
    addThumbstickAxesChangedListener(meshes, callback) {
        const id = ++this._idGenerator;
        this._thumbstickAwareMeshes[`${id}`] = { meshes, callback };
        return id;
    }
    addButtonStateChangedCallback(button, callback) {
        const id = ++this._idGenerator;
        this._buttonActions[`${id}`] = { button, callback };
        return id;
    }
    removeButtonStateChangedCallback(handler) {
        delete this._buttonActions[`${handler}`];
    }
    addAxesStateChangedCallback(axes, callback) {
        const id = ++this._idGenerator;
        this._axesActions[`${id}`] = { axes, callback };
        return id;
    }
    removeAxesStateChangedCallback(handler) {
        delete this._axesActions[`${handler}`];
    }
    removeThumbstickAxesChangedListener(handler) {
        delete this._thumbstickAwareMeshes[`${handler}`];
    }
    static addClickAddon(mesh) {
        const clickAddon = new ClickAddon();
        mesh[CLICK_ADDON_NAME] = clickAddon;
        return clickAddon;
    }
    static addPointerAddon(mesh) {
        const pointerAddon = new PointerAddonImpl(mesh);
        mesh[POINTER_ADDON_NAME] = pointerAddon;
        XRInputHelper.getInstance()?._pointerAddons.push(pointerAddon);
        return pointerAddon;
    }
}
